// Small form primitives for the auth pages. Restyled with the hangar in Phase 6.
import { colors, fonts, radii, spacing } from '@steelwake/ui';
import type { InputHTMLAttributes, ReactNode } from 'react';

export function Card({ children, width = 440 }: { children: ReactNode; width?: number }) {
  return (
    <section
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing.xl,
        maxWidth: width,
        width: '100%',
        margin: '0 auto',
      }}
    >
      {children}
    </section>
  );
}

export function PageTitle({ children }: { children: ReactNode }) {
  return (
    <h1 style={{ margin: `0 0 ${spacing.md}`, letterSpacing: '0.12em', fontSize: 22 }}>
      {children}
    </h1>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  hint?: string;
}

export function Field({ label, name, hint, ...input }: FieldProps) {
  // The hint lives outside the <label> so the input's accessible name is the label alone.
  const hintId = hint ? `${name}-hint` : undefined;
  return (
    <div style={{ marginBottom: spacing.md }}>
      <label
        htmlFor={name}
        style={{ display: 'block', color: colors.textMuted, fontSize: 13, marginBottom: 4 }}
      >
        {label}
      </label>
      <input
        name={name}
        id={name}
        aria-describedby={hintId}
        {...input}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.sm}`,
          background: colors.background,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: radii.sm,
          fontFamily: fonts.ui,
          fontSize: 15,
          ...input.style,
        }}
      />
      {hint ? (
        <span
          id={hintId}
          style={{ display: 'block', color: colors.textMuted, fontSize: 12, marginTop: 4 }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function Notice({
  kind,
  children,
  testId,
}: {
  kind: 'error' | 'success' | 'info';
  children: ReactNode;
  testId?: string;
}) {
  const color =
    kind === 'error' ? colors.danger : kind === 'success' ? colors.success : colors.textMuted;
  return (
    <p
      role={kind === 'error' ? 'alert' : 'status'}
      data-testid={testId}
      style={{ color, margin: `${spacing.sm} 0 ${spacing.md}` }}
    >
      {children}
    </p>
  );
}
